import { BaseAsset } from '@/modules/library/types';
import { LibraryDataProvider } from '@/modules/library/types/providers.types';
import { ASSET_CATEGORIES } from '@/types/assetTypes';
import { safeParseJson } from '@/utils/safeParseJson';
import { v1, type V1Result } from '@/infrastructure/api/v1/client';
import { listAll, listAllIn } from '@/infrastructure/api/v1/lists';
import { assetTypeToLegacy } from '@/infrastructure/api/v1/mappers/assetTypes';
import { assetInstanceToLegacy } from '@/infrastructure/api/v1/mappers/assetInstances';
import { uiElementTypeToLegacy } from '@/infrastructure/api/v1/mappers/uiElementTypes';
import { uiElementInstanceToLegacy } from '@/infrastructure/api/v1/mappers/uiElementInstances';

type AssetInstanceQuery = { sceneUniqueId?: string[]; projectUniqueId?: string; userUniqueId?: string; category?: string[] };

/** Asset instance rows matching the filters, in display order. */
async function listAssetInstanceRows(query: AssetInstanceQuery): Promise<V1Result<any[]>> {
  const { data, error } = await listAll(page => v1.GET('/api/v1/asset-instances', { params: { query: { ...query, ...page } } }));
  return error ? { data: null, error } : { data: data.map(assetInstanceToLegacy), error: null };
}

/** The caller's readable asset types by id, for the join the generic route could not do. */
async function assetTypesById(typeIds: string[]): Promise<V1Result<Map<string, any>>> {
  const { data, error } = await listAllIn(typeIds, (id, page) => v1.GET('/api/v1/asset-types', { params: { query: { uniqueId: id, ...page } } }));
  if (error) return { data: null, error };
  return { data: new Map(data.map(dto => [dto.uniqueId, assetTypeToLegacy(dto)])), error: null };
}

/** Asset instance rows with their asset type under `asset_types`; an instance whose type is not found is left out (an inner join). */
async function listAssetInstancesWithTypes(query: AssetInstanceQuery): Promise<any[]> {
  const instances = await listAssetInstanceRows(query);
  if (instances.error) throw instances.error;
  const typeIds = [...new Set(instances.data.map((i: any) => i.asset_type_id).filter(Boolean))] as string[];
  const types = await assetTypesById(typeIds);
  if (types.error) throw types.error;
  return instances.data
    .filter((instance: any) => types.data.has(instance.asset_type_id))
    .map((instance: any) => ({ ...instance, asset_types: types.data.get(instance.asset_type_id) }));
}

/** The signed-in user's own active Screen UI styles. */
async function myActiveUiElementTypes(): Promise<V1Result<any[]>> {
  const { data, error } = await listAll(page => v1.GET('/api/v1/ui-element-types', {
    params: { query: { owner: 'me', isActive: true, ...page } },
  }));
  return error ? { data: null, error } : { data: data.map(uiElementTypeToLegacy), error: null };
}

export class LibraryAdapter implements LibraryDataProvider {
  // Shared user-wide asset_types fetch: the editor preloader fans out over every
  // library category, so per-category requests multiply (~11 identical GETs per
  // scene load). TTL stays short so fresh uploads appear on the next library open
  // without needing cross-module cache invalidation.
  private static readonly MY_ASSET_TYPES_TTL_MS = 15_000;
  private myAssetTypes: { userId: string; fetchedAt: number; promise: Promise<any[]> } | null = null;

  invalidateMyAssetTypes(): void {
    this.myAssetTypes = null;
  }

  private fetchAllMyAssetTypes(userId: string): Promise<any[]> {
    const cached = this.myAssetTypes;
    if (cached && cached.userId === userId && Date.now() - cached.fetchedAt < LibraryAdapter.MY_ASSET_TYPES_TTL_MS) {
      return cached.promise;
    }
    // userId is the signed-in user's id, so their own asset types are listed
    const promise = (async () => {
      try {
        const { data, error } = await listAll(page => v1.GET('/api/v1/asset-types', { params: { query: { owner: 'me', ...page } } }));
        if (error) throw error;
        return data.map(assetTypeToLegacy);
      } catch (error) {
        if (this.myAssetTypes?.promise === promise) {
          this.myAssetTypes = null;
        }
        throw error;
      }
    })();
    this.myAssetTypes = { userId, fetchedAt: Date.now(), promise };
    return promise;
  }

  // Helper to map database response to BaseAsset
  private mapToAsset(instance: any, useTypeData: boolean = false): BaseAsset {
    if (useTypeData) {
      const assetType = instance.asset_types;
      return {
        typeId: assetType.asset_type_id,
        typeDisplayName: assetType.asset_type_display_name,
        typeImgUrl: assetType.asset_type_img_url,
        type: assetType.asset_type_category,
        createdAt: assetType.created_at,
        userId: assetType.user_id,
        audioSourceUrl: assetType.asset_type_category === 'audio' ? assetType.asset_type_source_url : undefined
      };
    } else {
      const isImage = instance.asset_instance_category === ASSET_CATEGORIES.IMAGE;
      const imageUrl = isImage ? instance.asset_instance_source_url : instance.asset_instance_img_url;
      const isAudio = instance.asset_instance_category === 'audio';
      
      return {
        typeId: instance.asset_instance_id,
        typeDisplayName: instance.asset_instance_display_name || instance.asset_instance_source_name || '',
        typeImgUrl: imageUrl,
        type: instance.asset_instance_category,
        assetTypeId: instance.asset_type_id, // For instance-based assets, use this for add-to-scene lookups
        createdAt: instance.asset_types?.created_at,
        userId: instance.asset_types?.user_id,
        audioSourceUrl: isAudio ? instance.asset_instance_source_url : undefined
      };
    }
  }

  // Audio and image instances of the user. No asset type is joined: the generic route dropped the
  // join, so these items carry no type creation date and keep their place in date-sorted lists.
  private async fetchAssetInstances(filters: { category: string, userId: string }): Promise<BaseAsset[]> {
    const { data, error } = await listAssetInstanceRows({ userUniqueId: filters.userId, category: [filters.category] });
    if (error) throw error;

    const is3D = ['prop', 'character', 'mask'].includes(filters.category);
    return data.map((instance: any) => this.mapToAsset(instance, is3D));
  }

  // Helper to remove duplicates by typeId
  private removeDuplicates(assets: BaseAsset[]): BaseAsset[] {
    return assets.filter((asset, index, self) => 
      index === self.findIndex(a => a.typeId === asset.typeId)
    );
  }

  // Helper to expand 3d category to individual categories
  private expandCategory(category: string): string[] {
    if (category === '3d') return ['prop', 'character'];
    // Note: 'image' category includes image_anchor for the combined image library
    // but 'image_anchor' alone is kept separate for the swap dialog
    if (category === 'image') return ['image', 'image_anchor'];
    // Keep immersal and image_anchor as single categories for swap dialog
    if (category === 'immersal') return ['immersal'];
    if (category === 'image_anchor') return ['image_anchor'];
    // 'anchor' is used by My Anchors section — includes all anchor types stored in asset_types
    if (category === 'anchor') return ['immersal', 'image_anchor', 'geospatial_anchor'];
    return [category];
  }

  async fetchProjectAssets(projectId: string, currentSceneId?: string): Promise<{ sceneAssets: BaseAsset[], projectAssets: BaseAsset[] }> {
    try {
      // Every instance in the project's scenes
      const instances = await listAssetInstanceRows({ projectUniqueId: projectId });
      if (instances.error) throw instances.error;

      // Fetch asset types for the instances (client-side join); a failure leaves the types out
      const typeIds = [...new Set(instances.data.map((i: any) => i.asset_type_id).filter(Boolean))] as string[];
      const assetTypes = await assetTypesById(typeIds);

      const sceneAssets: BaseAsset[] = [];
      const projectAssets: BaseAsset[] = [];

      instances.data.forEach((instance: any) => {
        // Use instance category or look up from asset type
        const assetType = assetTypes.data?.get(instance.asset_type_id);
        const category = instance.asset_instance_category || assetType?.asset_type_category;
        if (!category || ['geospatial_anchor', 'camera', 'clipAsset'].includes(category)) return;

        // Attach asset_types for mapToAsset compatibility
        instance.asset_types = assetType || {};

        const is3D = ['prop', 'character', 'mask'].includes(category);
        const useTypeData = is3D || category === 'immersal';
        const asset = this.mapToAsset(instance, useTypeData);

        if (currentSceneId && instance.scene_instance_id === currentSceneId) {
          sceneAssets.push(asset);
        } else {
          projectAssets.push(asset);
        }
      });

      // Include Screen UI instances: in current scene -> sceneAssets; in other scenes -> projectAssets (by type, deduped)
      const uiResponse = await listAll(page => v1.GET('/api/v1/ui-element-instances', {
        params: { query: { projectUniqueId: projectId, isActive: true, ...page } },
      }));
      if (!uiResponse.error && uiResponse.data.length) {
        const projectUIByType = new Set<string>();
        for (const ui of uiResponse.data.map(uiElementInstanceToLegacy)) {
          const base: BaseAsset = {
            typeId: ui.ui_element_type_id,
            typeDisplayName: ui.display_name || 'Screen Message',
            typeImgUrl: undefined,
            type: 'screen_ui',
            createdAt: undefined,
            userId: undefined,
          };
          if (currentSceneId && ui.scene_instance_id === currentSceneId) {
            sceneAssets.push(base);
          } else {
            if (!projectUIByType.has(ui.ui_element_type_id)) {
              projectUIByType.add(ui.ui_element_type_id);
              projectAssets.push(base);
            }
          }
        }
      }

      const uniqueSceneAssets = this.removeDuplicates(sceneAssets);
      const uniqueProjectAssets = this.removeDuplicates(projectAssets);

      return { sceneAssets: uniqueSceneAssets, projectAssets: uniqueProjectAssets };
    } catch (error) {
      console.error('Error fetching project assets:', error);
      return { sceneAssets: [], projectAssets: [] };
    }
  }

  async fetchMyAssets(userId: string): Promise<BaseAsset[]> {
    try {
      const allAssets: BaseAsset[] = [];

      // Fetch 3D assets from asset_types
      const myAssetTypes = await this.fetchAllMyAssetTypes(userId);

      const assetTypeAssets = myAssetTypes.map((assetType: any) => ({
        typeId: assetType.asset_type_id,
        typeDisplayName: assetType.asset_type_display_name,
        typeImgUrl: (assetType.asset_type_category === ASSET_CATEGORIES.IMAGE || assetType.asset_type_category === 'image_anchor') ? assetType.asset_type_source_url : assetType.asset_type_img_url,
        type: assetType.asset_type_category,
        createdAt: assetType.created_at,
        userId: assetType.user_id,
        audioSourceUrl: assetType.asset_type_category === 'audio' ? assetType.asset_type_source_url : undefined
      })).filter((asset: any) => {
        // For immersal/scan assets, only show ones with image URLs (GLB files with thumbnails)
        if (asset.type === 'immersal') {
          return asset.typeImgUrl !== null && asset.typeImgUrl !== '';
        }
        return true; // Show all other asset types
      });

      allAssets.push(...assetTypeAssets);

      // Fetch audio and image assets from asset_instances
      const assetInstanceAssets = await this.fetchAssetInstances({ 
        userId, 
        category: 'audio' 
      });
      allAssets.push(...assetInstanceAssets);

      const imageAssets = await this.fetchAssetInstances({
        userId,
        category: 'image'
      });
      allAssets.push(...imageAssets);

      // Include user's Screen UI element types (My UI Elements)
      const uiTypesResponse = await myActiveUiElementTypes();
      if (!uiTypesResponse.error && uiTypesResponse.data.length) {
        const uiAssets = uiTypesResponse.data.map((row: any) => ({
          typeId: row.type_id,
          typeDisplayName: row.display_name || row.source_name || 'Screen Message',
          typeImgUrl: undefined,
          type: 'screen_ui',
          createdAt: row.created_at,
          userId: row.org_member_id != null ? String(row.org_member_id) : undefined,
        } as BaseAsset));
        allAssets.push(...uiAssets);
      }

      return this.removeDuplicates(allAssets);
    } catch (error) {
      console.error('Error fetching my assets:', error);
      return [];
    }
  }

  async fetchAssetsByCategory(category: string, projectId?: string, _currentSceneId?: string): Promise<BaseAsset[]> {
    try {
      const categories = this.expandCategory(category);
      const allAssets: BaseAsset[] = [];

      for (const cat of categories) {
        const instances = await listAssetInstancesWithTypes({ projectUniqueId: projectId, category: [cat] });

        const is3D = ['prop', 'character', 'mask'].includes(cat);
        const assets = instances.map((instance: any) => this.mapToAsset(instance, is3D));
        allAssets.push(...assets);
      }

      return this.removeDuplicates(allAssets);
    } catch (error) {
      console.error(`Error fetching ${category} assets:`, error);
      return [];
    }
  }

  async fetchMyAssetsByCategory(category: string, projectId?: string, currentSceneId?: string, userId?: string): Promise<BaseAsset[]> {
    try {
      if (category === 'screen_ui') {
        if (!userId) return [];
        // userId is the signed-in user's id, so their own styles are listed
        const response = await myActiveUiElementTypes();
        if (response.error) throw response.error;
        return response.data.map((row: any) => ({
          typeId: row.type_id,
          typeDisplayName: row.display_name || row.source_name || 'Screen Message',
          typeImgUrl: undefined,
          type: 'screen_ui',
          createdAt: row.created_at,
          userId: row.org_member_id != null ? String(row.org_member_id) : undefined,
          styleBackgroundColor: row.style_background_color ?? undefined,
          styleTextColour: row.style_text_colour ?? undefined,
          styleCornerRadius: row.style_corner_radius ?? undefined,
          styleAlignment: row.style_alignment ?? undefined,
          styleJustify: row.style_justify ?? undefined,
          textBody: row.text_body ?? undefined,
        } as BaseAsset));
      }

      if (!userId) return [];

      const categories = this.expandCategory(category);
      const myAssetTypes = await this.fetchAllMyAssetTypes(userId);

      const allAssets = myAssetTypes
        .filter((assetType: any) => categories.includes(assetType.asset_type_category))
        .map((assetType: any) => {
          const cat = assetType.asset_type_category;
          return {
            typeId: assetType.asset_type_id,
            typeDisplayName: assetType.asset_type_display_name,
            typeImgUrl: (cat === 'image' || cat === 'image_anchor') ? assetType.asset_type_source_url : assetType.asset_type_img_url,
            type: cat,
            createdAt: assetType.created_at,
            userId: assetType.user_id,
            sourceUrl: assetType.asset_type_source_url,
            audioSourceUrl: cat === 'audio' ? assetType.asset_type_source_url : undefined,
            typeDetails: typeof assetType.asset_type_details === 'string'
              ? safeParseJson(assetType.asset_type_details)
              : assetType.asset_type_details,
          };
        })
        .filter((asset: any) => {
          // For immersal/scan assets, only show ones with image URLs (GLB files with thumbnails)
          if (asset.type === 'immersal') {
            return asset.typeImgUrl !== null && asset.typeImgUrl !== '';
          }
          return true; // Show all other asset types
        });

      return this.removeDuplicates(allAssets);
    } catch (error) {
      console.error(`Error fetching my ${category} assets:`, error);
      return [];
    }
  }

  async fetchAllAssetsByCategory(category: string, _userId?: string): Promise<BaseAsset[]> {
    try {
      if (category === 'screen_ui') {
        // Oldest first, the endpoint's order
        const response = await listAll(page => v1.GET('/api/v1/ui-element-types', {
          params: { query: { availability: 'public', isActive: true, ...page } },
        }));
        if (response.error) throw response.error;
        return response.data.map(uiElementTypeToLegacy).map((row: any) => ({
          typeId: row.type_id,
          typeDisplayName: row.display_name || row.source_name || 'Screen Message',
          typeImgUrl: undefined,
          type: 'screen_ui',
          createdAt: row.created_at,
          userId: row.org_member_id != null ? String(row.org_member_id) : undefined,
          styleBackgroundColor: row.style_background_color ?? undefined,
          styleTextColour: row.style_text_colour ?? undefined,
          styleCornerRadius: row.style_corner_radius ?? undefined,
          styleAlignment: row.style_alignment ?? undefined,
          styleJustify: row.style_justify ?? undefined,
          textBody: row.text_body ?? undefined,
        } as BaseAsset));
      }

      const categories = this.expandCategory(category);
      const allAssets: BaseAsset[] = [];

      for (const cat of categories) {
        if (cat === 'audio' || cat === 'image' || cat === 'image_anchor') {
          // For audio, image, and imageAnchor: fetch from asset_types with availability = public
          const response = await listAll(page => v1.GET('/api/v1/asset-types/library', {
            params: { query: { category: [cat], availability: 'public', ...page } },
          }));

          if (response.error) throw response.error;

          const assets = response.data.map(assetTypeToLegacy).map((assetType: any) => ({
            typeId: assetType.asset_type_id,
            typeDisplayName: assetType.asset_type_display_name,
            typeImgUrl: (cat === 'image' || cat === 'image_anchor') ? assetType.asset_type_source_url : assetType.asset_type_img_url,
            type: assetType.asset_type_category,
            createdAt: assetType.created_at,
            userId: assetType.user_id,
            audioSourceUrl: cat === 'audio' ? assetType.asset_type_source_url : undefined
          }));
          allAssets.push(...assets);
        } else {
          // The library browse set: public uploads and Lureo's listed items (display flag on)
          const response = await listAll(page => v1.GET('/api/v1/asset-types/library', {
            params: { query: { category: [cat], ...page } },
          }));

          if (response.error) throw response.error;

          const assets = response.data.map(assetTypeToLegacy).map((assetType: any) => ({
            typeId: assetType.asset_type_id,
            typeDisplayName: assetType.asset_type_display_name,
            typeImgUrl: assetType.asset_type_img_url,
            type: assetType.asset_type_category,
            createdAt: assetType.created_at,
            userId: assetType.user_id,
            sourceUrl: assetType.asset_type_source_url,
            audioSourceUrl: cat === 'audio' ? assetType.asset_type_source_url : undefined
          }));
          allAssets.push(...assets);
        }
      }

      return this.removeDuplicates(allAssets);
    } catch (error) {
      console.error(`Error fetching all ${category} assets:`, error);
      return [];
    }
  }

  async fetchSceneAssetsByCategory(category: string, currentSceneId: string): Promise<BaseAsset[]> {
    try {
      const categories = this.expandCategory(category);
      const allAssets: BaseAsset[] = [];

      for (const cat of categories) {
        const instances = await listAssetInstancesWithTypes({ sceneUniqueId: [currentSceneId], category: [cat] });

        const is3D = ['prop', 'character', 'mask'].includes(cat);
        const assets = instances.map((instance: any) => this.mapToAsset(instance, is3D));
        allAssets.push(...assets);
      }

      return this.removeDuplicates(allAssets);
    } catch (error) {
      console.error(`Error fetching scene ${category} assets:`, error);
      return [];
    }
  }

  async fetchAllAnchors(userId: string, sceneInstanceId: string): Promise<BaseAsset[]> {
    try {
      // Asset instances joined to asset_types, filtered by user_id on the joined type
      let instances: any[];
      try {
        instances = await listAssetInstancesWithTypes({
          sceneUniqueId: [sceneInstanceId],
          category: ['immersal', 'image_anchor', 'geospatial_anchor'],
        });
      } catch (error) {
        console.error('Error fetching asset instances:', error);
        throw error;
      }

      const filteredData = instances.filter((instance: any) => instance.asset_types?.user_id === userId);

      // Map to BaseAsset format - anchors use asset type data like immersal scans
      const assets = filteredData.map((instance: any) => this.mapToAsset(instance, true));
      const uniqueAssets = this.removeDuplicates(assets);
      return uniqueAssets;
    } catch (error) {
      console.error('Error fetching all anchors:', error);
      return [];
    }
  }
} 